import { useNavigate, useParams } from "react-router-dom";
import { formatDistanceToNowStrict } from "date-fns";
import { ArrowDownLeft, ArrowUpRight, Paperclip } from "lucide-react";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Skeleton } from "@/components/ui/skeleton";
import { cn, initials, avatarColor } from "@/lib/utils";
import type { InboxThreadCard } from "@/types/api";

interface Props {
  threads: InboxThreadCard[] | undefined;
  isLoading: boolean;
}

export default function ThreadList({ threads, isLoading }: Props) {
  const { conversationId } = useParams();
  const navigate = useNavigate();

  if (isLoading) {
    return (
      <div className="flex h-full flex-col gap-1 overflow-y-auto p-2 scrollbar-thin">
        {Array.from({ length: 8 }).map((_, i) => (
          <ThreadSkeleton key={i} />
        ))}
      </div>
    );
  }

  if (!threads || threads.length === 0) {
    return (
      <div className="flex h-full flex-col items-center justify-center px-6 text-center">
        <p className="font-display text-base font-semibold text-ink">Inbox zero</p>
        <p className="mt-1 text-sm text-ink-muted">
          No threads in this folder yet. Click <em>Sync mirror</em> in the toolbar to pull the
          latest from Microsoft 365.
        </p>
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col overflow-y-auto scrollbar-thin">
      {threads.map((t) => {
        const active = t.conversationId === conversationId;
        return (
          <button
            key={t.conversationId}
            onClick={() => navigate(`/inbox/${encodeURIComponent(t.conversationId)}`)}
            className={cn(
              "flex w-full gap-3 border-b border-line/70 px-3 py-2.5 text-left transition-colors",
              active
                ? "bg-brand/5 ring-1 ring-inset ring-brand/30"
                : "hover:bg-surface-hover",
              !t.isRead && !active && "bg-surface-panel",
            )}
          >
            <Avatar className="mt-0.5 h-8 w-8">
              <AvatarFallback className={avatarColor(t.fromAddress ?? t.fromName)}>
                {initials(t.fromName ?? t.fromAddress)}
              </AvatarFallback>
            </Avatar>

            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2">
                {!t.isRead && <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-brand" />}
                <span
                  className={cn(
                    "truncate text-sm",
                    t.isRead ? "text-ink-muted" : "font-semibold text-ink",
                  )}
                >
                  {t.fromName ?? t.fromAddress ?? "Unknown sender"}
                </span>
                <span className="ml-auto shrink-0 text-[11px] text-ink-dim">
                  {formatRelative(t.receivedAt)}
                </span>
              </div>

              <div className="mt-0.5 flex items-center gap-1.5">
                {t.isOutbound ? (
                  <ArrowUpRight className="h-3 w-3 shrink-0 text-success" />
                ) : (
                  <ArrowDownLeft className="h-3 w-3 shrink-0 text-accent-purple" />
                )}
                <span
                  className={cn(
                    "truncate text-[13px]",
                    t.isRead ? "text-ink-muted" : "text-ink",
                  )}
                >
                  {t.subject ?? "(no subject)"}
                </span>
                {t.hasAttachments && <Paperclip className="h-3 w-3 shrink-0 text-ink-dim" />}
              </div>

              {t.preview && (
                <p className="mt-0.5 truncate text-[12px] leading-snug text-ink-dim">
                  {t.preview}
                </p>
              )}
            </div>
          </button>
        );
      })}
    </div>
  );
}

function ThreadSkeleton() {
  return (
    <div className="flex gap-3 px-3 py-2.5">
      <Skeleton className="h-8 w-8 rounded-full" />
      <div className="flex-1 space-y-1.5">
        <Skeleton className="h-3 w-2/3" />
        <Skeleton className="h-3 w-full" />
        <Skeleton className="h-3 w-4/5" />
      </div>
    </div>
  );
}

function formatRelative(iso: string): string {
  try {
    return formatDistanceToNowStrict(new Date(iso), { addSuffix: false });
  } catch {
    return "";
  }
}
